import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardTable from './DashboardTable';
import IconButton from './IconButton';
import Pagination from '../../../components/UI/TableGrid/Pagination';

const EmployeeComplianceMonitor = ({ data = [], onActionClick, isLoading = false, onExpand }) => {
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [initialPosition, setInitialPosition] = useState(null);
  const containerRef = useRef(null);
  const originalPositionRef = useRef(null);
  const [allData, setAllData] = useState([]); // Store full data when in fullscreen
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    totalRecords: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [patientName, setPatientName] = useState('');
  const [sortColumn, setSortColumn] = useState('due_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Update data when component receives new data
  React.useEffect(() => {
    if (isFullscreen) {
      // When fullscreen, use the data (which should be full data from API)
      setAllData(data);
      // Reset pagination to page 1 when data changes (e.g., after search)
      setPagination(prev => ({ ...prev, page: 1, totalRecords: data.length }));
    } else {
      // When not fullscreen, use incoming data from parent (should be limited to 5)
      setTableData(data);
      setAllData([]);
      setPagination({ page: 1, perPage: 10, totalRecords: 0 });
      setSearchTerm('');
      setPatientName('');
      // Don't reset sortColumn/sortDirection - preserve user's sort selection
    }
  }, [data, isFullscreen]);

  // Apply pagination when in fullscreen (sort and search are API-based)
  React.useEffect(() => {
    if (isFullscreen) {
      if (allData.length === 0) {
        setTableData([]);
        setPagination(prev => ({ ...prev, totalRecords: 0, page: 1 }));
        return;
      }
      const totalRecords = allData.length;
      const maxPage = Math.max(1, Math.ceil(totalRecords / pagination.perPage));
      const currentPage = pagination.page > maxPage ? 1 : pagination.page;
      setPagination(prev => ({ ...prev, totalRecords, page: currentPage }));
      const start = (currentPage - 1) * pagination.perPage;
      const end = start + pagination.perPage;
      setTableData(allData.slice(start, end));
    }
  }, [allData, isFullscreen, pagination.page, pagination.perPage]);

  const handlePaginationChange = (newPagination) => {
    setPagination(newPagination);
  };

  // Open visit charting only for Employer Services (same as Visits screen)
  const isEmployerServicesCategory = (category) =>
    typeof category === 'string' && category.startsWith('Employer Services');

  const handleVisitTypeClick = (row) => {
    const visitId = row.visit_id || row.id;
    if (!visitId) return;
    navigate(`/visits/visit-details/${visitId}`);
  };

  const getCellContent = (col, row) => {
    if (col !== 'TYPE') return null;
    const category = row.visit_category ?? row.type ?? '';
    const typeLabel = category || '-';
    
    // DOT badge intentionally disabled to avoid extra DOT label in type column.
    // const dotBadge = row.is_dot ? (
    //   <span className="badge badge-info ml-2" style={{ fontSize: '0.7rem' }}>DOT</span>
    // ) : null;
    
    if (!isEmployerServicesCategory(category)) {
      // For non-Employer Services, just return the type with DOT badge if applicable
      return (
        <span>
          {typeLabel}
          {/* {dotBadge} */}
        </span>
      );
    }
    
    return (
      <span>
        <a
          href="#"
          className="text-dark "
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            handleVisitTypeClick(row);
          }}
          title="Open visit charting"
        >
          {typeLabel}
        </a>
        {/* {dotBadge} */}
      </span>
    );
  };

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
    if (onExpand) {
      onExpand(isFullscreen, patientName, column, direction);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle action clicks from DashboardTable
  const handleActionClick = (row, action) => {
    switch (action) {
      case 'schedule':
        console.log('row', row);
        // Extract employee_id from row data
        // Try multiple possible field names based on API response structure
        const employeeId = row.employee_id;
        if (employeeId) {
          navigate(`/appointments?employee_id=${employeeId}`);
        } else {
          navigate('/appointments');
        }
        break;
      default:
        // Fallback to parent's onActionClick if provided
        if (onActionClick) {
          onActionClick(row, action);
        }
    }
  };

  const SettingsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.4155 6.58674C10.1962 7.36741 10.1962 8.63474 9.4155 9.41674C8.63483 10.1974 7.3675 10.1974 6.5855 9.41674C5.80483 8.63608 5.80483 7.36874 6.5855 6.58674C7.3675 5.80474 8.63417 5.80474 9.4155 6.58674"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2 7.99992C2 7.56059 2.10133 7.12592 2.29733 6.72525V6.72525C3.30733 4.66059 5.53933 3.33325 8 3.33325C10.4607 3.33325 12.6927 4.66059 13.7027 6.72525V6.72525C13.8987 7.12592 14 7.56059 14 7.99992C14 8.43925 13.8987 8.87392 13.7027 9.27458V9.27458C12.6927 11.3393 10.4607 12.6666 8 12.6666C5.53933 12.6666 3.30733 11.3393 2.29733 9.27458V9.27458C2.10133 8.87392 2 8.43925 2 7.99992Z"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const CloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 4L4 12M4 4L12 12"
        stroke="#1D1917"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const handleToggleFullscreen = () => {
    if (!isFullscreen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const pos = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      };
      originalPositionRef.current = pos;
      setInitialPosition(pos);
      setIsFullscreen(true);
      setTimeout(() => {
        setInitialPosition(null);
      }, 50);
      // Fetch all data when expanding
      if (onExpand) {
        onExpand(true, '', sortColumn, sortDirection);
      }
    } else {
      if (originalPositionRef.current) {
        setInitialPosition(originalPositionRef.current);
        setTimeout(() => {
          setIsFullscreen(false);
          setInitialPosition(null);
          originalPositionRef.current = null;
          setPatientName(''); // Clear patient name when closing
          // Refetch with limit 5 when closing
          if (onExpand) {
            onExpand(false, '', sortColumn, sortDirection);
          }
        }, 300);
      } else {
        setIsFullscreen(false);
        setInitialPosition(null);
        setPatientName(''); // Clear patient name when closing
        // Refetch with limit 5 when closing
        if (onExpand) {
          onExpand(false, '', sortColumn, sortDirection);
        }
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${isFullscreen ? 'tblviewcard flip fullscreen-card' : 'col-lg-6 mb-2 pb-1'}`}
      style={isFullscreen ? {
        position: 'fixed',
        top: initialPosition ? `${initialPosition.top}px` : '0',
        left: initialPosition ? `${initialPosition.left}px` : '0',
        transform: initialPosition ? 'none' : 'none',
        width: initialPosition ? `${initialPosition.width}px` : '100vw',
        height: initialPosition ? `${initialPosition.height}px` : '100vh',
        zIndex: 1040,
        backgroundColor: '#fff',
        padding: initialPosition ? '20px' : '20px',
        borderRadius: initialPosition ? '8px' : '0',
        boxShadow: initialPosition ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
        transition: 'all 0.3s ease-in-out'
      } : { transition: 'all 0.3s ease-in-out' }}
    >
      <div className="card mb-0 border-0 shadow-none" style={isFullscreen ? { height: '100%', margin: 0 } : {}}>
        <div className="card-header sh_accordionheader">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="text-md font-weight-bold mb-0">Employee Compliance Monitor</h6>
            <div className="d-flex align-items-center gap-1">
              <div className="px-1">
                <a
                  className="btn sh_btniconnew"
                  style={{ cursor: 'pointer' }}
                  onClick={handleToggleFullscreen}
                  title={isFullscreen ? 'Minimize' : 'Maximize'}
                >
                  {isFullscreen ? <CloseIcon /> : <SettingsIcon />}
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className={`card-body ${isFullscreen ? '' : 'sh_dashtableoverflowcard'} px-0 pt-0 mt-2 pb-2`} style={isFullscreen ? { height: 'calc(100% - 60px)', overflow: 'auto', display: 'flex', flexDirection: 'column' } : {}}>
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              {/* <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div> */}
            </div>
          ) : (
            <>
              {isFullscreen && (
                <div className="px-3 pt-2 pb-2 d-flex align-items-center justify-content-end" style={{ borderBottom: '1px solid #e0e0e0' }}>
                  <div className="d-flex align-items-center">
                    <div className="mr-2">
                      <input
                        type="text"
                        placeholder="Employee"
                        value={patientName}
                        onChange={(e) => {
                          setPatientName(e.target.value);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            const trimmed = patientName.trim();
                            setPatientName(trimmed);
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, trimmed, sortColumn, sortDirection);
                            }
                          }
                        }}
                        className="form-control"
                        style={{ minWidth: '200px' }}
                      />
                    </div>
                    <div className="addAction">
                      <div className="btn-group">
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            const trimmed = patientName.trim();
                            setPatientName(trimmed);
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, trimmed);
                            }
                          }}
                        >
                          Search
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => {
                            setPatientName('');
                            setPagination(prev => ({ ...prev, page: 1 }));
                            if (onExpand) {
                              onExpand(true, '', sortColumn, sortDirection);
                            }
                          }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div style={isFullscreen ? { flex: 1, overflow: 'auto' } : {}}>
                <DashboardTable
                  columns={['EMPLOYEE', 'TYPE', 'VISIT DATE', 'DUE DATE', 'STATUS', 'ACTION']}
                  data={isFullscreen ? tableData : data}
                  onActionClick={handleActionClick}
                  sortable={true}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  emptyMessage="No employee compliance exists"
                  getCellContent={getCellContent}
                actionIcons={[
                  {
                    svg: (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.16797 9.58341H14.168" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.16797 12.9167H14.168" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.83203 9.58341H6.66536" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.83203 12.9167H6.66536" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6.2487 2.5V5" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.7487 2.5V5" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <rect x="2.5" y="3.75" width="15" height="13.75" rx="3" stroke="#4A505C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ),
                    title: 'Schedule',
                    action: 'schedule'
                  }
                ]}
                />
              </div>
              {isFullscreen && allData.length > 0 && pagination.totalRecords > pagination.perPage && (
                <div className="px-3 pb-2" style={{ borderTop: '1px solid #e0e0e0' }}>
                  <Pagination
                    pagination={pagination}
                    fetchTableRecords={handlePaginationChange}
                    tableRows={{ data: tableData }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeComplianceMonitor;

